import type { AllModuleTypeNames } from "@/types/modules";

export type ModuleChatSuggestions = readonly [string, string, string];

type SuggestionFamily =
  | "cartesian"
  | "proportional"
  | "tabular"
  | "textual"
  | "metric"
  | "media"
  | "process"
  | "structure"
  | "prompt";

export const FALLBACK_MODULE_CHAT_SUGGESTIONS: ModuleChatSuggestions = [
  "Rewrite the text more concisely",
  "Change the title",
  "Adjust the colors",
];

const SUGGESTIONS_BY_FAMILY: Record<SuggestionFamily, ModuleChatSuggestions> = {
  cartesian: [
    "Shorten the axis labels",
    "Keep only the key data points",
    "Render it in greyscale",
  ],
  proportional: [
    "Shorten the category labels",
    "Reduce it to fewer categories",
    "Render it in greyscale",
  ],
  tabular: [
    "Shorten the column headers",
    "Remove the least useful column",
    "Simplify the row labels",
  ],
  textual: [
    "Rewrite the text more concisely",
    "Shorten the headings",
    "Make the wording more direct",
  ],
  metric: [
    "Shorten the metric labels",
    "Round the numbers",
    "Render it in greyscale",
  ],
  media: [
    "Rewrite the caption",
    "Shorten the labels",
    "Improve the alt text",
  ],
  process: [
    "Shorten the step names",
    "Merge it into fewer steps",
    "Highlight the current step",
  ],
  structure: [
    "Shorten the node labels",
    "Trim it to fewer nodes",
    "Rename the groupings",
  ],
  prompt: [
    "Shorten the heading",
    "Reword the prompt more warmly",
    "Make the prompt more direct",
  ],
};

const FAMILY_BY_MODULE_TYPE: Record<AllModuleTypeNames, SuggestionFamily> = {
  bar: "cartesian",
  line: "cartesian",
  area: "cartesian",
  scatter: "cartesian",
  candlestick: "cartesian",
  "profit-loss": "cartesian",
  "metric-tabs-chart": "cartesian",

  pie: "proportional",
  funnel: "proportional",
  "radial-bar": "proportional",
  sunburst: "proportional",
  sankey: "proportional",
  radar: "proportional",
  heatmap: "proportional",
  "correlation-heatmap": "proportional",
  breakdown: "proportional",
  "stage-bars": "proportional",

  "feature-comparison": "tabular",
  "skill-level-matrix": "tabular",
  "pricing-tiers": "tabular",
  "split-comparison": "tabular",
  table: "tabular",

  intro: "textual",
  toc: "textual",
  quote: "textual",
  faq: "textual",
  "key-takeaways": "textual",
  "list-cards": "textual",
  "keywords-grouped": "textual",
  "keywords-explained": "textual",
  "activity-feed": "textual",

  progress: "metric",
  "metric-list": "metric",
  "big-number-chart": "metric",
  "social-proof-stats": "metric",
  "delta-comparison": "metric",
  "roi-calculator": "metric",
  "ring-kpis": "metric",
  "dual-metric": "metric",
  "insight-panel": "metric",

  image: "media",
  "logo-wall": "media",
  "dotted-map": "media",
  "before-after-annotated": "media",
  "before-after-slider": "media",

  "progress-stages": "process",
  "fork-join": "process",
  "gantt-phases": "process",
  "parallel-streams": "process",
  "segmented-progress": "process",
  "state-machine": "process",
  "timeline-stepper": "process",
  "node-cycle": "process",
  "segmented-cycle": "process",

  pyramid: "structure",
  "hierarchy-tree": "structure",
  "ecosystem-map": "structure",
  "parallel-category-trees": "structure",
  "quadrant-matrix": "structure",

  feedback: "prompt",

};

/**
 * Three starter prompts for the module chat, chosen by what a user plausibly
 * wants to change on that shape of module rather than per slug. Every entry is
 * something the module agent can do by editing this one module's content, so
 * nothing here promises a deck-level or styling change it cannot make. An
 * unknown or still-undefined type (a freshly dropped module) gets the generic
 * fallback.
 */
export function getModuleChatSuggestions(
  type?: string | null,
): ModuleChatSuggestions {
  if (!type) return FALLBACK_MODULE_CHAT_SUGGESTIONS;

  const family = FAMILY_BY_MODULE_TYPE[type as AllModuleTypeNames];
  if (!family) return FALLBACK_MODULE_CHAT_SUGGESTIONS;

  return SUGGESTIONS_BY_FAMILY[family];
}
